import { createFileRoute, notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import {
  CATEGORY_ESCAPE_ROOM,
  CATEGORY_HAUNT,
} from "@/components/game-categories/constants";
import { hasCategory } from "@/components/game-categories/helpers";
import apollo from "@/helpers/apollo";
import { cloudinaryImageUrl } from "@/helpers/cloudinary-url";
import Company from "@/pages/company";
import { QUERY } from "@/pages/company/graphql";
import type { DataType, VariablesType } from "@/pages/company/types";

const fetchCompany = createServerFn({ method: "GET" })
  .inputValidator((data: { slug: string }) => data)
  .handler(async ({ data }) => {
    const client = apollo();
    const response = await client.query<DataType, VariablesType>({
      fetchPolicy: "no-cache",
      query: QUERY,
      variables: { companySlug: data.slug, isUser: false },
    });

    if (!response.data.company) {
      throw notFound();
    }

    return { data: response.data };
  });

function getCompanyTitle(company: DataType["company"]): string {
  const hasEscapeRooms = company.locations.some((l) =>
    l.games.some((g) => hasCategory(g.primaryCategory, CATEGORY_ESCAPE_ROOM))
  );
  const hasHaunts = company.locations.some((l) =>
    l.games.some((g) => hasCategory(g.primaryCategory, CATEGORY_HAUNT))
  );

  if (hasEscapeRooms && hasHaunts) {
    return company.name;
  }
  if (hasEscapeRooms) {
    return company.name.toLowerCase().includes("escape")
      ? company.name
      : `${company.name} - Escape Room`;
  }
  if (hasHaunts) {
    return company.name.toLowerCase().includes("haunt")
      ? company.name
      : `${company.name} - Haunted Attraction`;
  }

  return company.name;
}

export const Route = createFileRoute("/company/$slug")({
  component: CompanyPage,
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {};
    }
    const { company } = loaderData.data;
    const title = getCompanyTitle(company);
    const url = `${process.env.APP_URL}/company/${company.slug}`;
    const imageId = company.locations[0]?.games[0]?.gameimageSet?.[0]?.imageId;

    return {
      links: [{ href: url, rel: "canonical" }],
      meta: [
        { title },
        { content: title, property: "og:title" },
        ...(imageId
          ? [{ content: cloudinaryImageUrl(imageId), property: "og:image" }]
          : []),
      ],
    };
  },
  loader: ({ params }) => fetchCompany({ data: { slug: params.slug } }),
});

function CompanyPage() {
  const { data } = Route.useLoaderData();
  return <Company data={data} />;
}
